import {
  Bijection,
  bijectionCompose,
  bijectionInvert,
  bijectionRef,
} from "@redotech/util/bijection";
import {
  base64BytesEncoding,
  base64Encoding,
  base64StringEncoding,
  latin1Encoding,
} from "@redotech/util/encoding";
import { STRING_ENUM } from "@redotech/util/enum";
import { Tuple } from "@redotech/util/type";
import { Json, jsonBufferFormat, JsonObject, jsonStringFormat } from "./json";

export interface JsonFormat<T> extends Bijection<Json, T> {}

export class JsonFormatError extends Error {
  override name = "JsonFormatError";

  constructor(
    readonly description: string,
    readonly path: (number | string)[],
  ) {
    super(description);

    if (this.path.length) {
      // deferred evaluation, for performance
      Object.defineProperty(this, "message", {
        get() {
          let pathString = "";
          for (const part of path) {
            // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
            switch (typeof part) {
              case "number":
                pathString += `[${part}]`;
                break;
              case "string":
                if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(part)) {
                  pathString += `[${JSON.stringify(part)}]`;
                  break;
                }
                if (pathString) {
                  pathString += ".";
                }
                pathString += part;
                break;
            }
          }
          return `${pathString}: ${description}`;
        },
      });
    }
  }
}

export const arrayBufferJsonFormat: JsonFormat<ArrayBuffer> = bijectionCompose(
  bijectionRef(() => stringJsonFormat),
  bijectionInvert(latin1Encoding),
);

export const arrayBufferBase64JsonFormat: JsonFormat<ArrayBuffer> =
  bijectionCompose(
    bijectionRef(() => stringJsonFormat),
    base64Encoding,
  );

export function arrayJsonFormat<T>(
  elementFormat: JsonFormat<T>,
): JsonFormat<T[]> {
  return {
    read(json: Json) {
      if (!(json instanceof Array)) {
        throw new JsonFormatError(`Expected array, got ${typeof json}`, []);
      }
      return json.map((element, index) => {
        try {
          return elementFormat.read(element);
        } catch (error) {
          if (error instanceof JsonFormatError) {
            throw new JsonFormatError(error.description, [
              index,
              ...error.path,
            ]);
          }
          throw error;
        }
      });
    },
    write(value: T[]) {
      return value.map((element) => elementFormat.write(element));
    },
  };
}

export function nullableJsonFormat<T>(
  format: JsonFormat<T>,
): JsonFormat<T | null> {
  return {
    read(json: Json) {
      if (json === null) {
        return null;
      }
      return format.read(json);
    },
    write(value: T | null) {
      if (value === null) {
        return null;
      }
      return format.write(value);
    },
  };
}

export const booleanJsonFormat: JsonFormat<boolean> = {
  read(json: Json) {
    if (typeof json !== "boolean") {
      throw new JsonFormatError(`Expected boolean, got ${typeof json}`, []);
    }
    return json;
  },
  write(value: boolean) {
    return value;
  },
};

export const numberJsonFormat: JsonFormat<number> = {
  read(json: Json) {
    if (typeof json !== "number") {
      throw new JsonFormatError(`Expected number, got ${typeof json}`, []);
    }
    return json;
  },
  write(value: number) {
    return value;
  },
};

export function objectJsonFormat<T extends object>(
  properties_: {
    [K in keyof T as undefined extends T[K] ? never : K]: JsonFormat<T[K]>;
  },
  optionalProperties_: {
    [K in keyof T as undefined extends T[K] ? K : never]-?: JsonFormat<
      Exclude<T[K], undefined>
    >;
  },
): JsonFormat<T> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const properties = Object.entries(properties_) as [
    keyof T,
    JsonFormat<any>,
  ][];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const optionalProperties = Object.entries(optionalProperties_) as [
    keyof T,
    JsonFormat<any>,
  ][];

  return {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        throw new JsonFormatError(
          `Expected object, got ${json instanceof Array ? "array" : typeof json}`,
          [],
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const result = {} as T;
      if (!isJsonObject(json)) {
        return result;
      }
      for (const [key, format] of properties) {
        if (!(key in json)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          throw new JsonFormatError(`Expected key: ${key as string}`, [
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            key as string,
          ]);
        }
        try {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          result[key] = format.read(json[key]);
        } catch (error) {
          if (error instanceof JsonFormatError) {
            throw new JsonFormatError(error.description, [
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              key as string,
              ...error.path,
            ]);
          }
          throw error;
        }
      }
      for (const [key, format] of optionalProperties) {
        if (!(key in json)) {
          continue;
        }
        try {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          result[key] = format.read(json[key]);
        } catch (error) {
          if (error instanceof JsonFormatError) {
            throw new JsonFormatError(error.description, [
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              key as string,
              ...error.path,
            ]);
          }
          throw error;
        }
      }
      return result;
    },
    write(value: T) {
      const json: JsonObject = {};
      for (const [key, format] of properties) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        json[key as string] = format.write(value[key]);
      }
      for (const [key, format] of optionalProperties) {
        if (value[key] === undefined) {
          continue;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        json[key as string] = format.write(value[key]);
      }
      return json;
    },
  };
}

export const shopifyGidJsonFormat: JsonFormat<`gid://shopify/${string}/${number}`> =
  {
    read(json: Json) {
      if (
        typeof json !== "string" ||
        !/^gid:\/\/shopify\/[^/]+\/\d+$/.test(json)
      ) {
        throw new Error(`Invalid Shopify GID: ${json}`);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return json as `gid://shopify/${string}/${number}`;
    },
    write(value: `gid://shopify/${string}/${number}`) {
      return value;
    },
  };

// Can be given a string representation of a number or a number, but will always return a number
export const stringOrNumberJsonFormat: JsonFormat<number> = {
  read(json: Json) {
    if (typeof json !== "string" && typeof json !== "number") {
      throw new JsonFormatError(
        `Expected string or number, got ${typeof json}`,
        [],
      );
    }
    const asNumber = Number(json);
    if (Number.isNaN(asNumber)) {
      throw new JsonFormatError(
        `Expected number or numeric string, got: ${json}`,
        [],
      );
    }
    return asNumber;
  },
  write(value: number) {
    return value;
  },
};

export const stringJsonFormat: JsonFormat<string> = {
  read(json: Json) {
    if (typeof json !== "string") {
      throw new JsonFormatError(`Expected string, got ${typeof json}`, []);
    }
    return json;
  },
  write(value: string) {
    return value;
  },
};

export function typedStringJsonFormat<T extends string>(str: T): JsonFormat<T> {
  return {
    read(json: Json) {
      if (typeof json !== "string" || json !== str) {
        throw new JsonFormatError(
          `Expected ${str} string, got ${typeof json === "string" ? JSON.stringify(json) : typeof json}`,
          [],
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return json as T;
    },
    write(value: string) {
      return value;
    },
  };
}

export function stringUnionJsonFormat<T extends readonly string[]>(
  values: T,
): JsonFormat<T[number]> {
  return bijectionCompose(stringJsonFormat, {
    read(value) {
      if (!values.includes(value as T[number])) {
        throw new JsonFormatError(
          `Expected enumerated string, got ${typeof value === "string" ? JSON.stringify(value) : typeof value}`,
          [],
        );
      }
      return value as T[number];
    },
    write(value) {
      return value;
    },
  });
}

export function stringEnumJsonFormat<T extends string>(
  mapping: Record<string, T>,
): JsonFormat<T> {
  const values = new Set(Object.values(mapping));
  return bijectionCompose(stringJsonFormat, {
    read(json: string) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      if (!values.has(json as T)) {
        throw new JsonFormatError(
          `Expected enumerated value, got ${JSON.stringify(json)}`,
          [],
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return json as T;
    },
    write(value: T) {
      return value;
    },
  });
}

export const dateJsonFormat = bijectionCompose(stringJsonFormat, {
  read: (string) => new Date(string),
  write: String,
});

const REGEXP_REGEXP = /^\/(.+)\/(\w+)?$/;

export const regexpJsonFormat: JsonFormat<RegExp> = bijectionCompose(
  stringJsonFormat,
  {
    read(string) {
      if (!string.startsWith("/")) {
        throw new JsonFormatError("Expected starting /", []);
      }
      const match = REGEXP_REGEXP.exec(string);
      if (!match) {
        throw new JsonFormatError(
          `Invalid regular expression: ${JSON.stringify(string)}`,
          [],
        );
      }
      const [_, pattern, flags] = match;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return new RegExp(pattern, flags);
    },
    write(regex) {
      return regex.toString();
    },
  },
);

export function stringMapJsonFormat<V>(
  value: JsonFormat<V>,
): JsonFormat<Map<string, V>> {
  return {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        throw new JsonFormatError(
          `Expected object, got ${json instanceof Array ? "array" : typeof json}`,
          [],
        );
      }
      const result = new Map<string, V>();
      if (!isJsonObject(json)) {
        return result;
      }
      for (const key in json) {
        let v: V;
        try {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          v = value.read(json[key]);
        } catch (error) {
          if (error instanceof JsonFormatError) {
            throw new JsonFormatError(error.description, [
              key as string,
              ...error.path,
            ]);
          }
          throw error;
        }
        result.set(key, v);
      }
      return result;
    },
    write(map: Map<string, V>) {
      const json: JsonObject = {};
      for (const [key, v] of map) {
        json[key] = value.write(v);
      }
      return json;
    },
  };
}

export function symbolJsonFormat<T extends symbol>(value: T): JsonFormat<T> {
  if (!value.description) {
    throw new Error("Description required for symbol");
  }
  return bijectionCompose(stringJsonFormat, {
    read(string: string) {
      if (string !== value.description) {
        throw new JsonFormatError(`Expected value: ${value.description}`, []);
      }
      return value;
    },
    write(symbol: T) {
      if (value !== symbol) {
        throw new JsonFormatError(`Expected value: ${String(value)}`, []);
      }
      return value.description;
    },
  });
}

export function symbolEnumJsonFormat<T extends symbol>(
  values: Iterable<T>,
): JsonFormat<T> {
  const byName = new Map<string, T>();
  for (const value of values) {
    if (value.description === undefined) {
      throw new Error("Description required for symbol");
    }
    if (byName.has(value.description)) {
      throw new Error(
        `Duplicate description for symbol: ${JSON.stringify(value.description)}`,
      );
    }
    byName.set(value.description, value);
  }
  return bijectionCompose(stringJsonFormat, {
    read(string: string) {
      const value = byName.get(string);
      if (!value) {
        throw new JsonFormatError(`Invalid value: ${string}`, []);
      }
      return value;
    },
    write(value: T) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return value.description!;
    },
  });
}

export function symbolUnionJsonFormat<T extends object>(
  jsonKey: string,
  valueKey: keyof T,
  formats: { [key: symbol]: JsonFormat<any> },
): JsonFormat<T> {
  for (const format of Object.getOwnPropertySymbols(formats)) {
    if (!format.description) {
      throw new Error("Symbol lacks description");
    }
  }
  const stringFormats: { [key: string]: JsonFormat<any> } = Object.fromEntries(
    Object.getOwnPropertySymbols(formats).map((key) => [
      key.description,
      formats[key],
    ]),
  );

  return {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        throw new JsonFormatError(
          `Expected object, got ${json instanceof Array ? "array" : typeof json}`,
          [],
        );
      }
      if (!(jsonKey in json) || !isJsonObject(json)) {
        throw new JsonFormatError(`Expected key: ${jsonKey}`, [
          jsonKey as string,
        ]);
      }
      const type = json[jsonKey];
      if (typeof type !== "string") {
        throw new JsonFormatError(`Expected string, got ${typeof type}`, [
          jsonKey as string,
        ]);
      }
      if (!(type in stringFormats)) {
        throw new JsonFormatError(`Unrecognized discriminator: ${type}`, [
          jsonKey as string,
        ]);
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return stringFormats[type].read(json);
    },
    write(value: T) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const type = value[valueKey] as symbol;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return formats[type].write(value);
    },
  };
}

export function discriminatedUnionFormat<
  TYPE extends { [key in DISCRIMINATOR_KEY]: DISCRIMINATOR },
  DISCRIMINATOR_KEY extends string,
  DISCRIMINATOR extends string,
>(
  jsonKey: DISCRIMINATOR_KEY,
  formats: Record<DISCRIMINATOR, JsonFormat<TYPE>>,
): JsonFormat<TYPE> {
  return {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        throw new JsonFormatError(
          `Expected object, got ${json instanceof Array ? "array" : typeof json}`,
          [],
        );
      }
      if (!(jsonKey in json) || !isJsonObject(json)) {
        throw new JsonFormatError(`Expected key: ${jsonKey}`, [
          jsonKey as string,
        ]);
      }
      const type = json[jsonKey];
      if (typeof type !== "string") {
        throw new JsonFormatError(`Expected string, got ${typeof type}`, [
          jsonKey as string,
        ]);
      }
      if (!(type in formats)) {
        throw new JsonFormatError(`Unrecognized discriminator: ${type}`, [
          jsonKey as string,
        ]);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return formats[type as DISCRIMINATOR].read(json);
    },
    write(value: TYPE) {
      const discriminator = value[jsonKey];
      return formats[discriminator].write(value);
    },
  };
}

/**
 * @param format1 MUST NOT BE ABLE TO SUCCEED if the type of the data is format2.
 *
 * Use with care.
 */
export function unionFormat<T, K, L = never>(
  format1: JsonFormat<T>,
  format2: JsonFormat<K>,
  determineFormat: (
    x: T | K | L,
  ) => JsonFormat<T> | JsonFormat<K> | JsonFormat<L>,
  format3?: JsonFormat<L>,
): JsonFormat<T | K | L> {
  return {
    read(json: Json) {
      try {
        return format1.read(json);
      } catch (error) {
        if (error instanceof JsonFormatError) {
          try {
            return format2.read(json);
          } catch (error) {
            if (error instanceof JsonFormatError && !!format3) {
              return format3.read(json);
            }
            throw error;
          }
        }
        throw error;
      }
    },
    write(value: T | K | L) {
      const format = determineFormat(value);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return format.write(value as T & K & L);
    },
  };
}

export function enumUnionJsonFormat<T extends object, E extends STRING_ENUM>(
  jsonKey: string,
  valueKey: keyof T,
  formats: { [key in keyof E]: JsonFormat<any> },
): JsonFormat<T> {
  for (const format of Object.getOwnPropertySymbols(formats)) {
    if (!format.description) {
      throw new Error("Symbol lacks description");
    }
  }

  return {
    read(json: Json) {
      if (typeof json !== "object" || json instanceof Array || !json) {
        throw new JsonFormatError("Expected object", []);
      }
      if (!(jsonKey in json) || !isJsonObject(json)) {
        throw new JsonFormatError(`Expected key: ${jsonKey}`, [
          jsonKey as string,
        ]);
      }
      const type = json[jsonKey];
      if (typeof type !== "string") {
        throw new JsonFormatError(`Expected string, got ${typeof type}`, [
          jsonKey as string,
        ]);
      }
      if (!(type in formats)) {
        throw new JsonFormatError(`Unrecognized discriminator: ${type}`, [
          jsonKey as string,
        ]);
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return formats[type].read(json);
    },
    write(value: T) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const type = value[valueKey] as keyof E;
      return formats[type].write(value);
    },
  };
}

export function backwardsCompatibleFormat<OLD, NEW>({
  oldFormat,
  newFormat,
  upgrade,
}: {
  oldFormat: JsonFormat<OLD>;
  newFormat: JsonFormat<NEW>;
  upgrade: (old: OLD) => NEW;
}): JsonFormat<NEW> {
  return {
    read(json: Json) {
      try {
        return newFormat.read(json);
      } catch (error) {
        if (error instanceof JsonFormatError) {
          return upgrade(oldFormat.read(json));
        }
        throw error;
      }
    },
    write(value: NEW) {
      return newFormat.write(value);
    },
  };
}

function isJsonObject(json: object | JsonObject): json is JsonObject {
  return (
    typeof json === "object" &&
    json !== null &&
    !Array.isArray(json) &&
    Object.entries(json).length > 0
  );
}

export function jsonSerializer<T>(format: JsonFormat<T>): Bijection<string, T> {
  return bijectionCompose(jsonStringFormat, format);
}

export const temporalDurationJsonFormat = bijectionCompose(stringJsonFormat, {
  read(value) {
    return Temporal.Duration.from(value);
  },
  write(value: Temporal.Duration): string {
    return value.toString();
  },
});

export const temporalInstantJsonFormat = bijectionCompose(stringJsonFormat, {
  read(value) {
    return Temporal.Instant.from(value);
  },
  write(value: Temporal.Instant): string {
    return value.toString();
  },
});

export const temporalPlainDateJsonFormat = bijectionCompose(stringJsonFormat, {
  read(value) {
    return Temporal.PlainDate.from(value);
  },
  write(value: Temporal.PlainDate): string {
    return value.toString();
  },
});

export const temporalPlainTimeJsonFormat = bijectionCompose(stringJsonFormat, {
  read(value) {
    return Temporal.PlainTime.from(value);
  },
  write(value: Temporal.PlainTime): string {
    return value.toString();
  },
});

export const temporalZonedDateTimeJsonFormat = bijectionCompose(
  stringJsonFormat,
  {
    read(value) {
      return Temporal.ZonedDateTime.from(value);
    },
    write(value: Temporal.ZonedDateTime): string {
      return value.toString();
    },
  },
);

export const urlJsonFormat: JsonFormat<URL> = bijectionCompose(
  stringJsonFormat,
  {
    read(string) {
      return new URL(string);
    },
    write(url) {
      return url.toString();
    },
  },
);

export function tupleJsonFormat<T, L extends number>(
  elementFormat: JsonFormat<T>,
  length: L,
): JsonFormat<Tuple<T, L>> {
  return bijectionCompose(arrayJsonFormat(elementFormat), {
    read(array) {
      if (array.length !== length) {
        throw new JsonFormatError(`Expected array of length ${length}`, []);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return array as Tuple<T, L>;
    },
    write(tuple) {
      return [...tuple];
    },
  });
}

export const neverJsonFormat: JsonFormat<never> = {
  read() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return null as never;
  },
  write(a: never) {
    return null;
  },
};

export function base64StringJsonFormat<T>(
  jsonFormat: JsonFormat<T>,
): Bijection<string, T> {
  return {
    read(encodedJsonString: string) {
      return jsonFormat.read(
        jsonStringFormat.read(base64StringEncoding.read(encodedJsonString)),
      );
    },
    write(value: T) {
      return base64StringEncoding.write(
        jsonStringFormat.write(jsonFormat.write(value)),
      );
    },
  };
}

/**
 * Like {@link base64StringJsonFormat}, but the JSON text crosses base64 as
 * UTF-8 bytes, so values containing non-latin1 characters round-trip
 * (`btoa`/`atob` in the plain string form throw on them).
 */
export function base64Utf8JsonFormat<T>(
  jsonFormat: JsonFormat<T>,
): Bijection<string, T> {
  return bijectionCompose(
    bijectionCompose(base64BytesEncoding, jsonBufferFormat),
    jsonFormat,
  );
}
